import { NotificationManager } from './NotificationManager'
import { SecretForm } from './SecretForm'
import { SecretView } from './SecretView'
import { Router } from '../utils/Router'
import { Header } from './Header'
import { Footer } from './Footer'
import { ThemeManager } from '../utils/ThemeManager'

interface SecretData {
  secret: string
  passphrase?: string
  ttl: number
  recipient?: string
}

interface AppState {
  currentView: 'create' | 'view' | 'retrieve'
  secretData?: SecretData
  retrieveKey?: string
  passphrase?: string
  secretKey?: string
}

export class App {
  private container: HTMLElement
  private state: AppState = { currentView: 'create' }
  private notificationManager: NotificationManager

  constructor(container: HTMLElement) {
    this.container = container
    this.notificationManager = new NotificationManager()
  }

  public init(initialRoute?: any): void {
    console.log('[App] Initializing with route:', initialRoute)

    ThemeManager.init()

    this.render()
    this.bindEvents()

    if (initialRoute) {
      this.handleRoute(initialRoute)
    }

    window.addEventListener('popstate', () => {
      const route = Router.parseRoute()
      this.handleRoute(route)
    })
  }

  private render(): void {
    this.container.innerHTML = `
      <div class="min-h-screen flex flex-col bg-gradient-to-br from-slate-50 via-white to-primary-50/40 dark:from-gray-950 dark:via-gray-900 dark:to-slate-900 relative overflow-hidden">
        <div class="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_80%_50%_at_50%_-20%,rgba(51,146,199,0.12),transparent)] dark:bg-[radial-gradient(ellipse_80%_40%_at_50%_-10%,rgba(51,146,199,0.15),transparent)]" aria-hidden="true"></div>
        <div class="pointer-events-none absolute top-1/4 -right-24 w-96 h-96 rounded-full bg-success-400/10 dark:bg-success-500/5 blur-3xl" aria-hidden="true"></div>
        <div class="relative flex flex-col flex-1 min-h-screen">
        ${new Header().render()}
        
        <main class="flex-1 flex items-center justify-center p-4">
          <div class="w-full max-w-2xl">
            <div id="app-content" class="animate-fade-in">
              ${this.renderCurrentView()}
            </div>
          </div>
        </main>

        ${new Footer().render()}
        
        <div id="notifications" class="fixed top-4 right-4 space-y-2 z-50"></div>
        </div>
      </div>
    `

    this.notificationManager.init(document.getElementById('notifications')!)

    ThemeManager.refreshIcon()

    const contentElement = document.getElementById('app-content')
    if (contentElement) {
      this.bindCurrentViewEvents(contentElement)
    }
  }

  private renderCurrentView(): string {
    switch (this.state.currentView) {
      case 'create':
        return new SecretForm(
          this.handleSecretCreated.bind(this),
          this.showError.bind(this)
        ).render()
      case 'view':
        return new SecretView(this.state.secretKey!, this.handleBackToForm.bind(this)).render()
      case 'retrieve':
        return this.renderRetrieveView()
      default:
        return new SecretForm(
          this.handleSecretCreated.bind(this),
          this.showError.bind(this)
        ).render()
    }
  }

  private bindEvents(): void {
    this.container.addEventListener('click', e => {
      const target = e.target as HTMLElement

      const actionElement = target.closest('[data-action]') as HTMLElement
      const action = actionElement?.dataset.action

      switch (action) {
        case 'back-to-form':
          this.handleBackToForm()
          break
        case 'reveal-secret':
          this.handleRevealSecret(e)
          break
        case 'toggle-retrieve-password':
          e.preventDefault()
          e.stopPropagation()
          this.toggleRetrievePasswordVisibility()
          break
        case 'toggle-theme':
          e.preventDefault()
          e.stopPropagation()
          ThemeManager.toggle()
          break
      }
    })
  }

  private bindCurrentViewEvents(container: HTMLElement): void {
    switch (this.state.currentView) {
      case 'create':
        const secretForm = new SecretForm(
          this.handleSecretCreated.bind(this),
          this.showError.bind(this)
        )
        secretForm.bindEvents(container)
        break
      case 'view':
        const secretView = new SecretView(this.state.secretKey!, this.handleBackToForm.bind(this))
        secretView.bindEvents(container)
        break
      case 'retrieve':
        const retrieveForm = container.querySelector('#retrieve-form') as HTMLFormElement
        retrieveForm?.addEventListener('submit', e => {
          e.preventDefault()
          this.handleRevealSecret(e)
        })
        break
    }
  }

  private handleSecretCreated(secretKey: string): void {
    this.setState({
      currentView: 'view',
      secretKey,
    })

    this.notificationManager.show({
      type: 'success',
      message: 'Secret created successfully!',
      duration: 3000,
    })
  }

  private handleBackToForm(): void {
    Router.goHome()
  }

  private toggleRetrievePasswordVisibility(): void {
    const input = document.getElementById('retrieve-passphrase') as HTMLInputElement
    if (input) {
      input.type = input.type === 'password' ? 'text' : 'password'
    }
  }

  private async handleRevealSecret(e?: Event): Promise<void> {
    if (e) {
      e.preventDefault()
    }

    if (!this.state.retrieveKey) {
      this.showError('Secret key not found')
      return
    }

    // Sempre pega a senha do campo de input, mesmo que esteja vazio
    const passphraseInput = document.getElementById('retrieve-passphrase') as HTMLInputElement
    const passphrase = passphraseInput?.value || this.state.passphrase || ''

    try {
      const { OTSService } = await import('../services/OTSService')
      const otsService = new OTSService()

      // Send passphrase only if non-empty, otherwise send undefined
      const passphraseToSend = passphrase.trim() !== '' ? passphrase.trim() : undefined

      const result = await otsService.retrieveSecret(this.state.retrieveKey, passphraseToSend)

      this.setState({
        currentView: 'view',
        secretKey: this.state.retrieveKey,
        retrieveKey: undefined,
      })

      this.showRetrievedSecret(result.secret)
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to retrieve secret'

      // Single message for any error
      this.showError(errorMessage)
      setTimeout(() => {
        Router.goHome()
      }, 3000)
    }
  }

  private showRetrievedSecret(secretContent: string): void {
    const contentElement = document.getElementById('app-content')
    if (contentElement) {
      contentElement.innerHTML = `
        <div class="bg-white/95 dark:bg-gray-900/95 backdrop-blur-sm rounded-2xl shadow-card border border-slate-100 dark:border-gray-700/90 p-8 animate-slide-up">
          <div class="text-center mb-8">
            <div class="w-16 h-16 bg-success-100 dark:bg-success-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg class="w-8 h-8 text-success-600 dark:text-success-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
              </svg>
            </div>
            <h1 class="text-3xl font-bold text-gray-900 dark:text-white mb-2">Secret Revealed</h1>
            <p class="text-gray-600 dark:text-gray-300">
              The content has been revealed. This link is now inactive.
            </p>
          </div>

          <div class="mb-6">
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Secret Content
            </label>
            <div class="relative">
              <textarea
                readonly
                class="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white font-mono text-sm resize-none"
                rows="6"
                id="revealed-secret"
              >${secretContent}</textarea>
              <button
                type="button"
                class="absolute top-3 right-3 btn btn-secondary btn-sm"
                onclick="navigator.clipboard.writeText(document.getElementById('revealed-secret').value); this.textContent = 'Copied!'; setTimeout(() => this.textContent = 'Copy', 2000)"
              >
                Copy
              </button>
            </div>
          </div>

          <div class="bg-warning-50 dark:bg-warning-900/20 border border-warning-200 dark:border-warning-800 rounded-lg p-4 mb-6">
            <div class="flex items-start">
              <svg class="w-5 h-5 text-warning-600 dark:text-warning-400 mt-0.5 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16c-.77.833.192 2.5 1.732 2.5z"></path>
              </svg>
              <div class="text-sm">
                <h4 class="font-medium text-warning-800 dark:text-warning-300 mb-1">Important</h4>
                <p class="text-warning-700 dark:text-warning-400">
                  This secret has been consumed and can no longer be accessed. The link is now inactive.
                </p>
              </div>
            </div>
          </div>

          <div class="text-center">
            <button
              type="button"
              class="btn btn-primary"
              onclick="window.location.href = '/'"
            >
              Create New Secret
            </button>
          </div>
        </div>
      `
    }
  }

  private setState(newState: Partial<AppState>): void {
    this.state = { ...this.state, ...newState }

    const contentElement = document.getElementById('app-content')
    if (contentElement) {
      contentElement.innerHTML = this.renderCurrentView()
      contentElement.className = 'animate-fade-in'

      this.bindCurrentViewEvents(contentElement)
    }
  }

  public showError(message: string): void {
    this.notificationManager.show({
      type: 'error',
      message,
      duration: 5000,
    })
  }

  public showSuccess(message: string): void {
    this.notificationManager.show({
      type: 'success',
      message,
      duration: 3000,
    })
  }

  private handleRoute(route: any): void {
    switch (route.type) {
      case 'home':
        this.setState({
          currentView: 'create',
          retrieveKey: undefined,
          passphrase: undefined,
          secretKey: undefined,
        })
        break
      case 'secret':
        if (route.params?.secretKey) {
          this.setState({
            currentView: 'retrieve',
            retrieveKey: route.params.secretKey,
            passphrase: route.params.passphrase,
            secretKey: undefined,
          })
        }
        break
      case 'notfound':
        this.showError('Page not found')
        this.setState({ currentView: 'create' })
        break
    }
  }

  private renderRetrieveView(): string {
    return `
      <div class="bg-white/95 dark:bg-gray-900/95 backdrop-blur-sm rounded-2xl shadow-card border border-slate-100 dark:border-gray-700/90 p-8 sm:p-10 animate-slide-up">
        <div class="text-center mb-8">
          <div class="w-16 h-16 bg-primary-100 dark:bg-primary-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg class="w-8 h-8 text-primary-600 dark:text-primary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"></path>
            </svg>
          </div>
          <h1 class="text-3xl font-bold text-gray-900 dark:text-white mb-2">Shared Secret</h1>
          <p class="text-gray-600 dark:text-gray-300">
            Enter the passphrase (if any) and click reveal to access the shared content.
            <span class="font-medium text-warning-600 dark:text-warning-400 block mt-1">Warning: can only be viewed once!</span>
          </p>
        </div>

        <form id="retrieve-form" class="space-y-6">
          <!-- Passphrase Input -->
          <div>
            <label for="retrieve-passphrase" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Secret Passphrase (optional)
            </label>
            <div class="relative">
              <input
                type="password"
                id="retrieve-passphrase"
                name="passphrase"
                class="w-full px-4 py-3 pr-12 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:focus:border-primary-400"
                placeholder="Enter the passphrase if the secret is protected..."
                autocomplete="off"
              />
              <button
                type="button"
                class="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300"
                data-action="toggle-retrieve-password"
              >
                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path>
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"></path>
                </svg>
              </button>
            </div>
          </div>

          <!-- Reveal Button -->
          <div class="text-center space-y-4">
            <button
              type="submit"
              class="btn btn-primary w-full"
              data-action="reveal-secret"
            >
              <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path>
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"></path>
              </svg>
              Reveal Secret
            </button>

            <button
              type="button"
              class="btn btn-outline w-full"
              onclick="window.location.href = '/'"
            >
              <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"></path>
              </svg>
              Create New Secret
            </button>

            <div class="text-sm text-gray-500 dark:text-gray-400">
              Key: <code class="bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded text-xs">${this.state.retrieveKey?.substring(0, 10)}...</code>
            </div>
          </div>
        </form>

        <div class="bg-warning-50 dark:bg-warning-900/20 border border-warning-200 dark:border-warning-800/50 rounded-lg p-4 mt-6">
          <div class="flex items-start">
            <svg class="w-5 h-5 text-warning-600 dark:text-warning-400 mt-0.5 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16c-.77.833.192 2.5 1.732 2.5z"></path>
            </svg>
            <div class="text-sm">
              <h4 class="font-medium text-warning-800 dark:text-warning-200 mb-1">Important</h4>
              <p class="text-warning-700 dark:text-warning-300">
                After viewing the content, it will be permanently deleted and cannot be accessed again.
              </p>
            </div>
          </div>
        </div>
      </div>
    `
  }
}
